import { Auth } from 'aws-amplify';
import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { IAuthProvider, ICurrentUser } from 'types/income';
import { auth } from 'auth';
import { msalPublicClientApplication } from './auth.utils';

interface GetAuthProvider {
  email: string;
}

interface SigninWithProvider {
  provider: string;
}

interface SigninWithMsal {
  email: string;
}

export const loaders = {
  getCurrentUser: () => {
    return api<ICurrentUser>({
      url: Endpoints.clientAdmin.companies.admins.me,
    });
  },
  getAuthProvider: ({ email }: GetAuthProvider) => {
    return api<IAuthProvider>({
      url: Endpoints.auth.provider,
      params: { email },
      useAuth: false,
    });
  },
  signinWithProvider: ({ provider }: SigninWithProvider) => {
    return Auth.federatedSignIn({ customProvider: provider });
  },
  signinWithMsal: async ({ email }: SigninWithMsal) => {
    const authResult = await msalPublicClientApplication.loginPopup({
      scopes: ['openid', 'profile', 'User.Read'],
      prompt: 'login',
      loginHint: email,
    });

    /* Cognito sign in */
    const username = authResult.account?.username?.toLowerCase();
    if (!username) {
      throw Error('Username is missing');
    }

    // Remove previously created device key to prevent authorization error.
    // See MIN-2633 for more details.
    const cacheKey = `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.${username}.deviceKey`;
    const deviceKey = window.localStorage.getItem(cacheKey);
    if (deviceKey) {
      window.localStorage.removeItem(cacheKey);
    }

    const token = authResult.idToken;

    // 1st request to Cognito - we initiate sign in without a password, which starts the custom authentication flow
    const initialUser = await Auth.signIn(username);

    if (
      initialUser.challengeName === 'CUSTOM_CHALLENGE' &&
      initialUser.challengeParam.challengeType === 'SELECT_CHALLENGE'
    ) {
      // 2nd request to Cognito - we instruct Cognito that we would like to login via MICROSOFT_SSO challenge
      const userAfterChallengeSelection = await Auth.sendCustomChallengeAnswer(
        initialUser,
        'SELECT_CHALLENGE',
        { challengeType: 'MICROSOFT_SSO' },
      );

      // 3rd request to Cognito - we send the Microsoft token for verification
      if (
        userAfterChallengeSelection.challengeParam.challengeType ===
        'MICROSOFT_SSO'
      ) {
        const userAfterChallengeAnswer = await Auth.sendCustomChallengeAnswer(
          userAfterChallengeSelection,
          token,
          { challengeType: 'MICROSOFT_SSO' },
        );

        return userAfterChallengeAnswer;
      }
    }

    throw new Error('Unsupported flow in Microsoft login');
  },
  getToken: async () => {
    const session = await Auth.currentSession();

    if (!session || !session.isValid()) return '';

    return session.getIdToken().getJwtToken();
  },
  changePassword: async ({
    oldPassword,
    newPassword,
  }: {
    oldPassword: string;
    newPassword: string;
  }) => {
    const currentUser = await Auth.currentAuthenticatedUser();
    return Auth.changePassword(currentUser, oldPassword, newPassword);
  },
  initPasswordReset: async ({ email }: { email: string }) => {
    return Auth.forgotPassword(email);
  },
  confirmPasswordReset: ({
    email,
    code,
    password,
  }: {
    email: string;
    code: string;
    password: string;
  }) => {
    return Auth.forgotPasswordSubmit(email, code, password);
  },
  completeNewPassword: ({ password }: { password: string }) => {
    return auth.completeNewPassword(password);
  },
  signout: () => {
    return auth.logout();
  },
};
